import React, { useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import useRequest from 'src/utils/hooks/useRequest';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { useRouter } from 'next/router';

interface LaunchPreviewButtonProps {
  launchPreviewRequested: boolean;
  size?: string;
}

const LaunchPreviewButton = ({ launchPreviewRequested, size = 'sm' }: LaunchPreviewButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { api } = useRequest();
  const { marketplace } = useContext(MarketplaceContext);
  const router = useRouter();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const onConfirmLaunch = async () => {
    if (!marketplace) return;
    setIsLoading(true);
    try {
      await api.marketplace.launchPreview(marketplace.id);
      onClose();
      router.reload();
    } catch (error) {
      let errorMessage = 'Could not launch preview.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        colorScheme={launchPreviewRequested ? 'gray' : 'blue'}
        variant="outline"
        data-test="launch-preview-btn"
        isDisabled={launchPreviewRequested}
        onClick={onOpen}
        size={size}
        ml={2}
        isLoading={isLoading}
      >
        {launchPreviewRequested ? 'On Launch Waitlist' : 'Ready to Launch?'}
      </Button>
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Launch Your Preview
            </AlertDialogHeader>
            <AlertDialogBody>
              Yes, I have fully configured my preview and it is ready for live traffic from my
              audience.
              <br />
              <br />
              Please launch my web app!
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                No, not yet
              </Button>
              <Button colorScheme="blue" onClick={onConfirmLaunch} ml={3}>
                Yes, I am ready
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default LaunchPreviewButton;
